import rawTransformers from '../data/transformers.json';
import { Transformer } from '../types/transformer';

/**
 * Source catalog data contains a few duplicate slugs (product variants that
 * ended up with identical SEO slugs in the DB export). A duplicate slug makes
 * every position after the first unreachable, because the detail page resolves
 * a product by an exact slug match (`Array.find`).
 *
 * This helper keeps ALL positions and disambiguates colliding slugs with a
 * numeric suffix (`-2`, `-3`, ...), so each transformer gets a unique, reachable
 * URL. The original JSON file is left untouched.
 *
 * Iteration order follows the JSON file order, so the resulting slugs are
 * deterministic and identical to the ones produced by the sitemap script.
 */
function withUniqueSlugs(items: Transformer[]): Transformer[] {
  const seen = new Map<string, number>();

  return items.map((item) => {
    const baseSlug = item.slug?.trim() ?? '';
    if (baseSlug === '') return item;

    const previousCount = seen.get(baseSlug) ?? 0;
    seen.set(baseSlug, previousCount + 1);

    // First occurrence keeps the original slug; later ones get a suffix.
    if (previousCount === 0) return item;
    return { ...item, slug: `${baseSlug}-${previousCount + 1}` };
  });
}

/** All catalog transformers with guaranteed-unique, reachable slugs. */
export const transformers: Transformer[] = withUniqueSlugs(
  rawTransformers as unknown as Transformer[]
);
